import { FC, useEffect, useMemo } from 'react';

import { Input, Modal, Select, Tabs } from 'antd';
import Search from 'antd/es/input/Search';
import Title from 'antd/es/typography/Title';
import { paymentLookupTypeOptions } from 'common/constants';
import { TPaymentLookupType } from 'common/types/payment-diagnostics';
import { observer } from 'mobx-react-lite';

import { DiagnosticsDrawer } from './DiagnosticsDrawer';
import { IncidentsTab } from './IncidentsTab';
import { PaymentLogsViewModel } from './payment-logs-view-model';
import { WebhookRequestsTab } from './WebhookRequestsTab';

import styles from './payment-logs.module.scss';

interface IPaymentLogsProps {
  model: PaymentLogsViewModel;
}

const lookupPlaceholders: Record<TPaymentLookupType, string> = {
  order: 'Enter order number',
  attempt: 'Enter payment attempt ID',
  intent: 'Enter provider intent ID',
};

const PaymentLogs: FC<IPaymentLogsProps> = observer(({ model }) => (
  <div className={styles.page}>
    <Title level={5} type="secondary" className={styles.title}>
      Payments logs
    </Title>

    <div className={styles.lookup}>
      <Select
        value={model.lookupType}
        options={paymentLookupTypeOptions}
        className={styles.lookupType}
        onChange={model.setLookupType}
      />
      <Search
        allowClear
        enterButton="Look up"
        value={model.lookupQuery}
        placeholder={lookupPlaceholders[model.lookupType]}
        loading={model.detailLoading}
        className={styles.lookupInput}
        onChange={(e) => model.setLookupQuery(e.target.value)}
        onSearch={model.submitLookup}
      />
    </div>

    <Tabs
      defaultActiveKey="incidents"
      items={[
        {
          key: 'incidents',
          label: 'Open incidents',
          children: <IncidentsTab model={model} />,
        },
        {
          key: 'webhook-requests',
          label: 'Webhook requests',
          children: <WebhookRequestsTab model={model} />,
        },
      ]}
    />

    <DiagnosticsDrawer model={model} />

    <Modal
      open={model.isResolveOpen}
      title={
        model.resolveIds.length === 1
          ? 'Close incident'
          : `Close ${model.resolveIds.length} incidents`
      }
      okText="Close"
      okButtonProps={{ danger: true }}
      confirmLoading={model.resolveLoading}
      /**
       * The drawer shares the default z-index of 1000 and, being destroyed on
       * close, gets re-mounted after this modal in the DOM - which would leave
       * the modal underneath it once the drawer has been reopened.
       */
      zIndex={1100}
      onOk={model.confirmResolve}
      onCancel={model.closeResolveModal}
    >
      <p>
        The incidents stay in the log as resolved. The reason below is saved to
        the payment timeline together with your name.
      </p>
      {/* The character counter is positioned below the field, so it needs room
          of its own or it lands on the footer buttons. */}
      <div className={styles.resolveReason}>
        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          value={model.resolveReason}
          placeholder="Reason (optional), e.g. payment failed on the provider side"
          onChange={(e) => model.setResolveReason(e.target.value)}
        />
      </div>
    </Modal>
  </div>
));

export const PaymentLogsPage = () => {
  const model = useMemo(() => new PaymentLogsViewModel(), []);

  useEffect(() => {
    model.fetchIncidents();
    model.fetchWebhookRequests();
  }, [model]);

  return <PaymentLogs model={model} />;
};
